import { useMemo, useRef } from 'react';

import { TASK_CONST } from '@/constant';
import type { TASK_WORKSTATE } from '@/constant/taskConstant';
import { useTypedSearchParams } from '@/hook';
import type { VTableColumnFilter, VTableData } from '@/package';
import { StickyNote2, VFlex, VTable, cn, useVModal } from '@/package';
import type { Task, TaskSearchParams, WorkProgress } from '@/type';

import type { TaskTableProps } from './type';

const TaskTable = <T extends { [x: string]: any }>({
  cellRenderer,
  columnList,
  columnStyleMap,
  columnRenderer,

  enabledChecked,
  enabledRemove,
  hasNextPage,
  isFetchingNextPage,
  selectedTaskIDList,
  taskList,

  fetchNextPage,
  selectTaskList,

  onCellClick,
  onRowClick,
  onRemove,
}: TaskTableProps<T>) => {
  const { openDangerConfirm } = useVModal();
  const rowRef = useRef<HTMLDivElement>(null);
  const isAllSelected =
    selectedTaskIDList.length &&
    taskList.every(t => selectedTaskIDList.find(tl => tl === t.id));
  const { getAll, remove, append, flush } =
    useTypedSearchParams<TaskSearchParams>();

  const renderedColumnList = useMemo(() => {
    let renderedColumnList = [] as (
      | 'checked'
      | 'remove'
      | keyof With<Task, T>
    )[];

    enabledChecked && renderedColumnList.push('checked');
    columnList?.length &&
      (renderedColumnList = renderedColumnList.concat(columnList));
    enabledRemove && renderedColumnList.push('remove');

    return renderedColumnList;
  }, [enabledChecked, enabledRemove, columnList]);

  const renderedTaskList = useMemo(() => {
    return taskList.map(t => {
      return {
        ...t,
        id: `${t.id}`,
        checked: Boolean(
          enabledChecked ? selectedTaskIDList.find(id => t.id === id) : null,
        ),
        remove: null,
      };
    });
  }, [enabledChecked, enabledRemove, taskList, selectedTaskIDList]);

  const onDeleteClick = (task: Task & T) => {
    openDangerConfirm({
      title: 'Delete Task',
      message: () => (
        <VFlex direction="col" className="gap-[10px]">
          <div className="text-body-xl text-black">
            All data will be permanently deleted. Are you sure you want to
            proceed?
          </div>

          <VFlex className="items-center gap-1 text-gray-500">
            <StickyNote2 className="h-4 w-4" />
            <div className="w-full flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {task.orgPath}
            </div>
          </VFlex>
        </VFlex>
      ),
      onConfirm: () => onRemove?.(+task.id),
      confirmText: 'Delete',
    });
  };

  const onTaskCheckboxClick = (task: Task & T) => {
    const taskId = +task.id;
    const idx = selectedTaskIDList.findIndex(p => p === taskId);
    if (idx === -1) {
      selectTaskList([...selectedTaskIDList, taskId]);
    } else {
      selectTaskList(selectedTaskIDList.filter((_, i) => i !== idx));
    }
  };

  const onHeaderCheckboxClick = () => {
    isAllSelected
      ? selectTaskList([])
      : selectTaskList([...(taskList.map(v => v.id) || [])]);
  };

  const mapWorkProgressToTaskState = (
    workProgress: WorkProgress,
  ): TASK_WORKSTATE[] => {
    if (workProgress === 'In progress')
      return [TASK_CONST.TASK_WORKSTATE.PROGRESS];
    if (workProgress === 'Completed')
      return [TASK_CONST.TASK_WORKSTATE.COMPLETED];
    if (workProgress === 'Failed') return [TASK_CONST.TASK_WORKSTATE.FAIL];
    return [
      TASK_CONST.TASK_WORKSTATE.NONE,
      TASK_CONST.TASK_WORKSTATE.PENDING,
      TASK_CONST.TASK_WORKSTATE.READY,
    ];
  };

  const statusFilterItemList = (() => {
    const filterItemList = [];

    const searchParams = getAll('taskTaskState');

    filterItemList.push({
      label: 'Not started',
      value: 'Not started',
      isSelected: [
        TASK_CONST.TASK_WORKSTATE.NONE,
        TASK_CONST.TASK_WORKSTATE.PENDING,
        TASK_CONST.TASK_WORKSTATE.READY,
      ].every(state => searchParams.includes(state)),
    });

    filterItemList.push({
      label: 'In progress',
      value: 'In progress',
      isSelected: searchParams.includes(TASK_CONST.TASK_WORKSTATE.PROGRESS),
    });

    filterItemList.push({
      label: 'Completed',
      value: 'Completed',
      isSelected: searchParams.includes(TASK_CONST.TASK_WORKSTATE.COMPLETED),
    });

    filterItemList.push({
      label: 'Failed',
      value: 'Failed',
      isSelected: searchParams.includes(TASK_CONST.TASK_WORKSTATE.FAIL),
    });

    return filterItemList;
  })();

  const columnFilter = [
    {
      id: 'status',
      itemList: statusFilterItemList,
    },
  ];

  return (
    <>
      <VTable
        ref={rowRef}
        rowSize="lg"
        data={renderedTaskList}
        onRowCheckedChange={row => {
          const task = taskList.find(t => t.id === +row.id);
          task && onTaskCheckboxClick(task);
        }}
        onAllRowCheckedChange={onHeaderCheckboxClick}
        className={cn({ 'h-full': !!taskList.length })}
        cellRenderer={cellRenderer}
        columnList={renderedColumnList as any}
        columnStyleMap={columnStyleMap}
        columnRenderer={columnRenderer}
        columnFilter={columnFilter as VTableColumnFilter<VTableData>[]}
        setRowSelected={() => true}
        setRowChecked={row => selectedTaskIDList.includes(+row.id)}
        setRowRemovable={() => true}
        onCellClick={e => onCellClick?.(e.target as any)}
        onRowClick={e => onRowClick?.(e.target as any)}
        onRemove={id =>
          onDeleteClick(renderedTaskList.find(task => task.id === id)!)
        }
        onScrollEnd={() => {
          hasNextPage && !isFetchingNextPage && fetchNextPage?.();
        }}
        onColumnFilterChange={columnFilter => {
          const statusColumnFilter = columnFilter.find(
            filter => filter.id === 'status',
          );
          const statusColumnFilterItemList = statusColumnFilter?.itemList;
          remove('taskTaskState');
          statusColumnFilterItemList?.forEach(item => {
            if (!item.isSelected) return;

            const label = item.label as WorkProgress;
            const taskState = mapWorkProgressToTaskState(label);
            taskState.forEach(state => {
              append('taskTaskState', state);
            });
          });
          flush();
        }}
      />
    </>
  );
};

export default TaskTable;
